import React, { useEffect } from "react";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/Reducer";

interface InitialValues {
  supplyDemand: string;
  precursorCostChange: number;
  stopLimit: number;
  goInPosition: number;
  rounds: number;
  NegotiationTarget: number;
  paymentTerms: string;
  incoTerms: string;
  localStock: string;
}
interface TargetValueProps {
  setIsNextDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setRefInstance: (instance: FormikProps<any> | null) => void;
  isDetailedOverview?: boolean;
}

const PercentageInputField = ({
  label,
  name,
  formik,
}: {
  label: string;
  name: keyof InitialValues;
  formik: FormikProps<InitialValues>;
}) => {
  const adjustValue = (direction: "inc" | "dec") => {
    const raw = formik.values[name];
    const current = typeof raw === "number" && !isNaN(raw) ? raw : parseFloat(raw as any);
    const safeCurrent = isNaN(current) ? 0 : current;
    const updated = +(safeCurrent + (direction === "inc" ? 0.1 : -0.1)).toFixed(1);
    formik.setFieldValue(name, updated);
  };

  return (
    <div className="price-range">
      <span className="me-2" style={{ alignContent: "center" }}>
        {label}
      </span>
      <div>
        <div className="IncDecBtn">
          <button
            className="plus-minus-icons"
            type="button"
            onClick={() => adjustValue("dec")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              fill="#004E8C"
              className="bi bi-dash-circle"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
              <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8" />
            </svg>
          </button>
          <div className="Percentage-Input">
            <div className="textinput-label">Input in %</div>
            <input
              type="number"
              name={name}
              className="form-control form-control-sm mx-2 no-spinner"
              style={{ width: "80px", textAlign: "center" }}
              value={formik.values[name] as number}
              onChange={formik.handleChange}
              onBlur={() => {
                let val = formik.values[name] as number;
                if (isNaN(val)) val = 0;
                formik.setFieldValue(name, val);
              }}
            />
          </div>
          <button
            className="plus-minus-icons"
            style={{ background: "none", border: "none", alignItems: "center", justifyContent: "center", height: "25px", cursor: "pointer" }}
            type="button"
            onClick={() => adjustValue("inc")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              fill="#004E8C"
              className="bi bi-plus-circle"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
            </svg>
          </button>
        </div>
        {formik.errors[name] && (
          <div className="error-text" style={{ justifyContent: "space-around" }}>
            {formik.errors[name]}
          </div>
        )}
      </div>
    </div>
  );
};

const TargetValue = ({ setIsNextDisabled, setRefInstance, isDetailedOverview = false }: TargetValueProps) => {
  const targetValuesData = useSelector((state: RootState) => state.TargetValue.TargetValueData);
  const powerPositionType = useSelector((state: RootState) => state.powerPosition.powerPosition);
  const salesRepNegotiationType = useSelector((state: RootState) => state.salesRepClassification.classificationType);
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  const initialValues: InitialValues = {
    supplyDemand: "",
    precursorCostChange: 0,
    stopLimit: 0,
    goInPosition: 0,
    rounds: 3,
    NegotiationTarget: 0,
    paymentTerms: "",
    incoTerms: "",
    localStock: "",
  };
  const validationSchema = Yup.object({
    supplyDemand: Yup.string().trim().required("This is a required field"),
    precursorCostChange: Yup.number()
      .min(-1000, "Value should be greater than or equal to -1000")
      .max(1000, "Value should be less than or equal to 1000")
      .required("This is a required field")
      .test(
        "max-decimals",
        "Maximum 2 decimal places allowed",
        (value) =>
          value === undefined || /^-?\d+(\.\d{1,2})?$/.test(String(value))
      ),
    stopLimit: Yup.number()
      .min(-1000, "Value should be greater than or equal to -1000")
      .max(1000, "Value should be less than or equal to 1000")
      .required("This is a required field")
      .test(
        "max-decimals",
        "Maximum 2 decimal places allowed",
        (value) =>
          value === undefined || /^-?\d+(\.\d{1,2})?$/.test(String(value))
      ),
    goInPosition: Yup.number()
      .min(-1000, "Value should be greater than or equal to -1000")
      .max(1000, "Value should be less than or equal to 1000")
      .required("This is a required field")
      .test(
        "max-decimals",
        "Maximum 2 decimal places allowed",
        (value) =>
          value === undefined || /^-?\d+(\.\d{1,2})?$/.test(String(value))
      ),
    NegotiationTarget: Yup.number()
      .min(-1000, "Value should be greater than or equal to -1000")
      .max(1000, "Value should be less than or equal to 1000")
      .required("This is a required field")
      .test(
        "max-decimals",
        "Maximum 2 decimal places allowed",
        (value) =>
          value === undefined || /^-?\d+(\.\d{1,2})?$/.test(String(value))
      ),
    paymentTerms: Yup.string().max(1000, "Maximum 1000 characters allowed").nullable().notRequired(),
    incoTerms: Yup.string().max(1000, "Maximum 1000 characters allowed").nullable().notRequired(),
    localStock: Yup.string().max(1000, "Maximum 1000 characters allowed").nullable().notRequired(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: () => { },
  });

  useEffect(() => {
    setIsNextDisabled(!formik.isValid);
  }, [formik.isValid]);

  useEffect(() => {
    setRefInstance(formik);
  }, [formik]);

  useEffect(() => {
    formik.setValues({ ...formik.values, ...targetValuesData });
  }, []);


  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <h5 className="Target-heading" style={{ display: "flex", marginLeft: "6rem" }}>
          Please define your targets for this negotiation:
        </h5>
        <div className="Target-value-Conatiner">

          {/* 1. Supply-Demand */}
          <div className="gray-containers">
            <div className="text-column">
              <div className="text-label">
                Please describe the <b>supply-demand balance</b> for the negotiated product in the market.
              </div>
            </div>
            <div className="target-options">
              {["Long", "Balanced", "Short"].map((option) => (
                <div className="custom-radio" key={option}>
                  <input
                    type="radio"
                    name="supplyDemand"
                    id={option}
                    value={option}
                    checked={formik.values.supplyDemand === option}
                    onChange={() => formik.setFieldValue("supplyDemand", option)}
                  />
                  <label className="form-check-label" htmlFor={option}>
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Precursor Cost Change */}
          <div className="gray-container-second">
            <div className="text-column">
              <div className="text-label">
                Quantify the expected <b>change of precursor-costs</b> in this negotiation period.
              </div>
            </div>
            <PercentageInputField label="Price change" name="precursorCostChange" formik={formik} />
          </div>
          {/* 3. Negotiation Target */}
          <div className="gray-containers">
            <div className="First-Column">
              <div className="text-column">
                <div className="text-label">
                  Indicate your <b>negotiation target(s)</b> based on:
                  <li>Supply demand balance</li>
                  <li>Precursor-cost change</li>
                  <li>Power position (<b>{powerPositionType}</b>)</li>
                  <li>Your sales rep's negotiation tactics (<b>{salesRepNegotiationType}</b>)</li>
                </div>
              </div>
            </div>
            <div className="second-Column">
              <PercentageInputField label="Price change" name="NegotiationTarget" formik={formik} />
              {["paymentTerms", "incoTerms", "localStock"].map((field) => (
                <div className="price-range2" key={field}>
                  <div className="input-title">
                    <span className="me-2">{capitalize(field)}</span>
                  </div>
                  <div className="input-box">
                    <input
                      type="text"
                      name={field}
                      className="form-control form-control-sm"
                      value={(formik.values as any)[field]}
                      onChange={formik.handleChange}
                    />
                    {formik.errors[field as keyof InitialValues] && (
                      <div className="error-text">{formik.errors[field as keyof InitialValues]}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* 4. Stop Limit */}
          <div className="gray-container-second">
            <div className="text-column">
              <div className="text-label">
                Indicate your <b>stop-limit.</b> <br /> (worst outcome you would still accept to not need to switch to a different supplier)
              </div>
            </div>
            <PercentageInputField label="Price change" name="stopLimit" formik={formik} />
          </div>
          {/* 5. Go-In Position */}
          <div className="gray-containers">
            <div className="text-column">
              <div className="text-label">
                Indicate your <b>go-in position</b> for the upcoming negotiation.
              </div>
            </div>
            <PercentageInputField label="Price change" name="goInPosition" formik={formik} />
          </div>
          {/* 6. Rounds */}
          <div className="gray-container-second">
            <div className="text-column">
              <div className="text-label">
                Indicate the <b>number of negotiation rounds</b> you plan to
                conduct.
                <br></br>(Experience shows that negotiation results almost
                always improve till at least the third round of negotiation)
              </div>
            </div>
            <div className="rounds">
              <span className="me-2">Rounds</span>
              <select
                className="form-select form-select-sm"
                style={{
                  width: "80px",
                  height: "33px",
                  cursor: isDetailedOverview ? "default" : "pointer",
                  paddingLeft: "24px",
                  marginRight: "4.8rem",
                }}
                name="rounds"
                value={formik.values.rounds}
                onChange={formik.handleChange}
                disabled={isDetailedOverview}
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TargetValue;
